import React, { useEffect, useRef, useState } from "react";
import {
  FaMicrophone, FaStop, FaUpload, FaPlay, FaPause,
  FaCheckCircle, FaTrash, FaSpinner, FaExclamationTriangle, FaClock,
  FaChevronDown, FaChevronUp, FaInfoCircle,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API = `${process.env.REACT_APP_API_BASE_URL}/api/student/audio-submissions`;
const BATCH_API = `${process.env.REACT_APP_API_BASE_URL}/api/student/my-batch`;

// ── Topics ────────────────────────────────────────────────────────────────────
interface Topic { title: string; prompts: string[] }

const TOPICS: Record<"A1" | "A2" | "B1" | "B2", Topic[]> = {
  A1: [
    { title: "Sich vorstellen (Introducing Yourself)", prompts: ["Wie heißt du und woher kommst du?", "Was machst du beruflich?", "Was sind deine Hobbys?"] },
    { title: "Meine Familie (My Family)", prompts: ["Wie groß ist deine Familie?", "Hast du Geschwister?", "Wie heißen deine Eltern?"] },
    { title: "Mein Zuhause (My Home)", prompts: ["Wo wohnst du?", "Wie viele Zimmer hat deine Wohnung?", "Magst du dein Zuhause?"] },
    { title: "Essen und Trinken (Food & Drinks)", prompts: ["Was isst du zum Frühstück?", "Was ist dein Lieblingsessen?", "Kochst du gern?"] },
    { title: "Meine Hobbys (My Hobbies)", prompts: ["Was machst du in deiner Freizeit?", "Treibst du Sport?", "Hörst du gern Musik?"] },
    { title: "Mein Tag (My Day)", prompts: ["Wann stehst du auf?", "Was machst du morgens?", "Wann gehst du schlafen?"] },
    { title: "Einkaufen (Shopping)", prompts: ["Gehst du gern einkaufen?", "Was kaufst du oft?", "Welche Geschäfte magst du?"] },
    { title: "Das Wetter (The Weather)", prompts: ["Wie ist das Wetter heute?", "Was ist dein Lieblingswetter?", "Magst du Winter oder Sommer?"] },
    { title: "Tiere (Animals)", prompts: ["Hast du ein Haustier?", "Welche Tiere magst du?", "Hast du Angst vor Tieren?"] },
    { title: "Meine Stadt (My City)", prompts: ["In welcher Stadt lebst du?", "Was gefällt dir in deiner Stadt?", "Was fehlt in deiner Stadt?"] },
    { title: "Farben und Kleidung (Clothes)", prompts: ["Was trägst du heute?", "Was ist deine Lieblingsfarbe?", "Kaufst du gern Kleidung?"] },
    { title: "Zahlen und Uhrzeit (Numbers & Time)", prompts: ["Wie spät ist es jetzt?", "Wann stehst du morgens auf?", "Um wie viel Uhr beginnt deine Arbeit?"] },
    { title: "Mein Körper (My Body)", prompts: ["Welche Körperteile kennst du auf Deutsch?", "Treibst du Sport für deine Gesundheit?", "Was machst du, wenn du Kopfschmerzen hast?"] },
    { title: "Kranksein (Being Sick)", prompts: ["Was machst du, wenn du krank bist?", "Gehst du zum Arzt oder kaufst du Medikamente?", "Wie lange warst du zuletzt krank?"] },
    { title: "Transportmittel (Transport)", prompts: ["Wie kommst du zur Arbeit oder Schule?", "Fährst du lieber mit dem Bus oder dem Fahrrad?", "Gibt es guten öffentlichen Verkehr in deiner Stadt?"] },
    { title: "Meine Freunde (My Friends)", prompts: ["Wie viele Freunde hast du?", "Was machst du mit deinen Freunden?", "Hast du einen besten Freund?"] },
    { title: "Geburtstag und Feste (Birthdays & Celebrations)", prompts: ["Wann hast du Geburtstag?", "Wie feierst du deinen Geburtstag?", "Was sind deine Lieblingsferien?"] },
    { title: "Sport und Bewegung (Sport)", prompts: ["Welchen Sport machst du gern?", "Wie oft gehst du Sport?", "Schaust du gern Sport im Fernsehen?"] },
    { title: "Musik und Filme (Music & Films)", prompts: ["Was für Musik hörst du gern?", "Was ist dein Lieblingsfilm?", "Gehst du ins Kino?"] },
    { title: "Im Supermarkt (At the Supermarket)", prompts: ["Wie oft gehst du in den Supermarkt?", "Was kaufst du meistens?", "Welcher Supermarkt ist dein Lieblingssupermarkt?"] },
    { title: "Im Restaurant (At the Restaurant)", prompts: ["Gehst du oft ins Restaurant?", "Was bestellst du am liebsten?", "Wie ist der Service normalerweise?"] },
    { title: "Berufe (Professions)", prompts: ["Was ist dein Beruf?", "Was möchtest du in Zukunft arbeiten?", "Was ist ein interessanter Beruf für dich?"] },
    { title: "Mein Traumhaus (My Dream Home)", prompts: ["Wie sieht dein Traumhaus aus?", "Wo würde es stehen?", "Was ist das Wichtigste in einem Zuhause?"] },
    { title: "Im Park (At the Park)", prompts: ["Gehst du gern in den Park?", "Was machst du dort?", "Gibt es einen schönen Park in deiner Stadt?"] },
    { title: "Wochentage und Routine (Weekdays)", prompts: ["Was machst du montags?", "Was ist dein Lieblingstag der Woche?", "Was machst du am Wochenende?"] },
    { title: "Begrüßungen und Vorstellung (Introductions)", prompts: ["Wie stellst du dich vor?", "Was sagst du, wenn du jemanden kennenlernst?", "Wie begrüßt man in Deutschland?"] },
    { title: "Zahlen im Alltag (Numbers in Daily Life)", prompts: ["Wie viel kostet dein Mittagessen?", "Wie weit ist es von deinem Zuhause zur Arbeit?", "Wie viele Stunden schläfst du?"] },
    { title: "Mein Lieblingsort (My Favourite Place)", prompts: ["Was ist dein Lieblingsort in Deutschland?", "Warum magst du diesen Ort?", "Was machst du dort?"] },
    { title: "Jahreszeiten (Seasons)", prompts: ["Was ist deine Lieblingszeit des Jahres?", "Was machst du im Sommer?", "Wie ist der Winter in Deutschland?"] },
    { title: "Auf der Straße (On the Street)", prompts: ["Wie fragst du nach dem Weg auf Deutsch?", "Hast du dich schon mal verlaufen?", "Wie ist die Umgebung wo du wohnst?"] },
    { title: "Im Café (At the Café)", prompts: ["Gehst du gern ins Café?", "Was trinkst du am liebsten im Café?", "Mit wem gehst du ins Café?"] },
    { title: "Mein Zimmer (My Room)", prompts: ["Wie sieht dein Zimmer aus?", "Was steht in deinem Zimmer?", "Hast du ein eigenes Zimmer?"] },
    { title: "Obst und Gemüse (Fruit and Vegetables)", prompts: ["Welches Obst magst du?", "Isst du gern Gemüse?", "Was kaufst du oft im Supermarkt?"] },
    { title: "Im Kino (At the Cinema)", prompts: ["Gehst du gern ins Kino?", "Was ist dein Lieblingsfilm?", "Was für Filme magst du?"] },
    { title: "Mein Morgen (My Morning)", prompts: ["Was machst du als Erstes am Morgen?", "Was frühstückst du normalerweise?", "Wie lange brauchst du, um fertig zu werden?"] },
    { title: "Im Zug (On the Train)", prompts: ["Fährst du oft mit dem Zug?", "Wohin bist du zuletzt mit dem Zug gefahren?", "Was machst du im Zug?"] },
    { title: "Meine Schule (My School)", prompts: ["Bist du noch in der Schule oder studierst du?", "Was war dein Lieblingsfach in der Schule?", "Wie war deine Schule?"] },
    { title: "Haushalt (Household Chores)", prompts: ["Welche Hausarbeit machst du?", "Kochst du selbst?", "Wer putzt bei dir zu Hause?"] },
    { title: "Mein Lieblingstier (My Favourite Animal)", prompts: ["Welches Tier magst du am liebsten?", "Hast du Angst vor Tieren?", "Welches Tier findest du lustig?"] },
    { title: "Am Telefon (On the Phone)", prompts: ["Wie oft telefonierst du?", "Mit wem telefonierst du am meisten?", "Schreibst du lieber Nachrichten oder rufst du an?"] },
    { title: "Mein Abend (My Evening)", prompts: ["Was machst du abends?", "Schaust du abends fern?", "Wann gehst du normalerweise ins Bett?"] },
    { title: "Im Hotel (At the Hotel)", prompts: ["Warst du schon mal in einem Hotel?", "Was ist wichtig in einem Hotel für dich?", "Wie war dein letzter Hotelaufenthalt?"] },
    { title: "Farben (Colours)", prompts: ["Was ist deine Lieblingsfarbe?", "Welche Farben trägst du oft?", "Welche Farbe magst du nicht?"] },
    { title: "Auf dem Markt (At the Market)", prompts: ["Gehst du manchmal auf den Markt?", "Was kaufst du auf dem Markt?", "Wie ist der Markt in deiner Stadt?"] },
    { title: "Meine Lieblingsmusik (My Favourite Music)", prompts: ["Was für Musik hörst du gern?", "Hörst du Musik beim Lernen?", "Welchen Sänger oder welche Band magst du?"] },
    { title: "Begrüßung und Abschied (Greetings and Farewells)", prompts: ["Wie begrüßt du Freunde?", "Was sagst du, wenn du dich verabschiedest?", "Wie begrüßt man Fremde auf Deutsch?"] },
    { title: "Mein Wochenende (My Weekend)", prompts: ["Was machst du am Wochenende?", "Schläfst du am Wochenende länger?", "Triffst du Freunde am Wochenende?"] },
    { title: "Im Büro (At the Office)", prompts: ["Arbeitest du in einem Büro?", "Was machst du bei der Arbeit?", "Wie ist dein Arbeitsplatz?"] },
    { title: "Kinder (Children)", prompts: ["Hast du Kinder?", "Was spielst du gern mit Kindern?", "Was war dein Lieblingsspielzeug als Kind?"] },
    { title: "Meine Lieblingsmahlzeit (My Favourite Meal)", prompts: ["Was ist deine Lieblingsmahlzeit?", "Kochst du das selbst?", "Wann isst du am liebsten?"] },
    { title: "In der Apotheke (At the Pharmacy)", prompts: ["Gehst du oft in die Apotheke?", "Was kaufst du in der Apotheke?", "Nimmst du Medikamente?"] },
    { title: "Das Alphabet (The Alphabet)", prompts: ["Kannst du das deutsche Alphabet aufsagen?", "Welche deutschen Buchstaben sind neu für dich?", "Wie buchstabierst du deinen Namen auf Deutsch?"] },
    { title: "Meine Nachbarn (My Neighbours)", prompts: ["Wie sind deine Nachbarn?", "Kennst du deine Nachbarn?", "Sprichst du manchmal mit deinen Nachbarn?"] },
    { title: "Im Garten (In the Garden)", prompts: ["Hast du einen Garten?", "Was wächst in einem deutschen Garten?", "Was machst du gern draußen?"] },
    { title: "Sprachen (Languages)", prompts: ["Welche Sprachen sprichst du?", "Warum lernst du Deutsch?", "Ist Deutsch schwer für dich?"] },
    { title: "Meine Tasche (My Bag)", prompts: ["Was trägst du in deiner Tasche?", "Hast du eine große oder kleine Tasche?", "Was ist immer in deiner Tasche?"] },
    { title: "Auf dem Weg zur Arbeit (Commute)", prompts: ["Wie kommst du zur Arbeit oder Schule?", "Wie lange dauert dein Weg?", "Was machst du auf dem Weg?"] },
    { title: "Mein Lieblingsgetränk (My Favourite Drink)", prompts: ["Was trinkst du am liebsten?", "Trinkst du viel Wasser?", "Was trinkst du morgens?"] },
    { title: "Zahlen üben (Practising Numbers)", prompts: ["Wie alt bist du?", "Wie ist deine Telefonnummer?", "Wie viel kostet eine Fahrkarte in deiner Stadt?"] },
  ],
  A2: [
    { title: "Öffentliche Verkehrsmittel (Public Transport)", prompts: ["Wie fährst du zur Arbeit?", "Nimmst du oft den Bus oder die Bahn?", "Was sind die Vorteile des ÖPNV?"] },
    { title: "Urlaub und Reisen (Travel)", prompts: ["Wohin bist du zuletzt gereist?", "Was machst du im Urlaub am liebsten?", "Reist du lieber im Sommer oder Winter?"] },
    { title: "Freunde und Freizeit", prompts: ["Was machst du mit deinen Freunden?", "Gehst du oft aus?", "Was ist dein Lieblingsort für Treffen?"] },
    { title: "Arbeit und Beruf (Work)", prompts: ["Was ist dein Beruf?", "Was magst du an deiner Arbeit?", "Wie sieht dein Arbeitsalltag aus?"] },
    { title: "Gesundheit und Sport", prompts: ["Treibst du regelmäßig Sport?", "Wie bleibst du gesund?", "Warst du kürzlich beim Arzt?"] },
    { title: "Restaurant und Café", prompts: ["Gehst du gern essen?", "Was bestellst du oft?", "Welche Küche magst du am liebsten?"] },
    { title: "Deutsche Traditionen", prompts: ["Welche deutschen Feiertage kennst du?", "Hast du Weihnachtsmarkt besucht?", "Was findest du interessant an der deutschen Kultur?"] },
    { title: "Schule und Lernen", prompts: ["Wie lernst du Deutsch?", "Was war dein Lieblingsfach in der Schule?", "Liest du gern Bücher?"] },
    { title: "Wohnen in Deutschland", prompts: ["Wie ist es, in Deutschland zu leben?", "Was ist anders als in deiner Heimat?", "Was vermisst du aus deiner Heimat?"] },
    { title: "Einkaufen in Deutschland", prompts: ["Wo kaufst du Lebensmittel ein?", "Hast du schon auf dem Markt eingekauft?", "Wie sind die Preise in Deutschland?"] },
    { title: "Beim Arzt (At the Doctor)", prompts: ["Wie machst du einen Arzttermin?", "Was sagst du, wenn du Schmerzen hast?", "Hast du schon mal einen Spezialisten besucht?"] },
    { title: "Bank und Finanzen (Banking)", prompts: ["Hast du ein deutsches Bankkonto?", "Wie bezahlst du meistens – bar oder mit Karte?", "Was sind typische Ausgaben im Monat?"] },
    { title: "Behörden und Ämter (Offices)", prompts: ["Welche Ämter hast du in Deutschland besucht?", "Wie war deine Erfahrung beim Bürgeramt?", "Welche Dokumente brauchst du oft?"] },
    { title: "Deutschkurs und Sprachlernen", prompts: ["Wie lernst du Deutsch?", "Was findest du am schwersten auf Deutsch?", "Welche Methoden helfen dir am meisten?"] },
    { title: "Haushalt und Putzen (Household)", prompts: ["Wie oft putzt du deine Wohnung?", "Wer macht den Haushalt bei dir?", "Was ist deine liebste und ungeliebteste Hausarbeit?"] },
    { title: "Küche und Kochen (Cooking)", prompts: ["Kochst du selbst oder kaufst du Fertigessen?", "Was kochst du am liebsten?", "Hast du schon ein deutsches Gericht gekocht?"] },
    { title: "Feste und Feiertage in Deutschland", prompts: ["Welche deutschen Feiertage kennst du?", "Wie hast du Weihnachten in Deutschland erlebt?", "Was machst du an Feiertagen?"] },
    { title: "Nachbarschaft (Neighbourhood)", prompts: ["Wie sind deine Nachbarn?", "Sprichst du oft mit deinen Nachbarn?", "Was gefällt dir in deiner Nachbarschaft?"] },
    { title: "Smartphone und Internet", prompts: ["Wie lange nutzt du täglich dein Smartphone?", "Welche Apps benutzt du am meisten?", "Wie hilft dir das Internet beim Leben in Deutschland?"] },
    { title: "Natur und Umwelt (Nature)", prompts: ["Gehst du gern in die Natur?", "Welche Naturorte hast du in Deutschland besucht?", "Wie wichtig ist Umweltschutz für dich?"] },
    { title: "Medien und Nachrichten (Media)", prompts: ["Wie informierst du dich über aktuelle Ereignisse?", "Schaust du deutsches Fernsehen?", "Welche Nachrichtenquelle vertraust du am meisten?"] },
    { title: "Familie und Beziehungen", prompts: ["Wie oft siehst du deine Familie?", "Wie hältst du Kontakt mit der Familie in der Heimat?", "Was vermisst du am meisten an deiner Familie?"] },
    { title: "Reisen innerhalb Deutschlands", prompts: ["Welche deutschen Städte hast du besucht?", "Wie reist du am liebsten – Zug, Auto oder Bus?", "Welche Stadt möchtest du noch besuchen?"] },
    { title: "Mode und Stil (Fashion)", prompts: ["Wie würdest du deinen Kleidungsstil beschreiben?", "Kaufst du lieber im Geschäft oder online?", "Was trägst du im Sommer?"] },
    { title: "Freizeitaktivitäten in Deutschland", prompts: ["Was machst du in deiner Freizeit in Deutschland?", "Bist du Mitglied in einem Verein?", "Welche Aktivitäten hast du neu entdeckt?"] },
    { title: "Schreiben auf Deutsch (Writing in German)", prompts: ["Schreibst du oft auf Deutsch?", "Was ist schwieriger – schreiben oder sprechen?", "Schreibst du E-Mails oder Nachrichten auf Deutsch?"] },
    { title: "Meine Zukunftspläne (Future Plans)", prompts: ["Was sind deine Ziele für das nächste Jahr?", "Möchtest du in Deutschland bleiben?", "Was möchtest du beruflich erreichen?"] },
    { title: "Post und Pakete (Post Office)", prompts: ["Schickst du oft Pakete in dein Heimatland?", "Wie funkioniert die Post in Deutschland?", "Hast du schon mal ein Paket verloren?"] },
    { title: "Tiere und Natur in Deutschland", prompts: ["Welche Tiere sieht man oft in Deutschland?", "Hast du ein Haustier?", "Was denkst du über Tierschutz?"] },
    { title: "Mein Alltag in Deutschland (Daily Life)", prompts: ["Wie sieht ein normaler Tag für dich aus?", "Was ist anders als in deiner Heimat?", "Was magst du am deutschen Alltag?"] },
    { title: "Öffnungszeiten und Geschäfte (Opening Hours)", prompts: ["Wann sind Supermärkte in Deutschland geöffnet?", "Was ist sonntags geschlossen?", "Hast du dich mal über Öffnungszeiten geärgert?"] },
    { title: "Müll trennen (Recycling)", prompts: ["Trennst du deinen Müll?", "Welche Tonnen gibt es in Deutschland?", "Was passiert mit dem Pfand?"] },
    { title: "Wetter und Kleidung (Weather and Clothes)", prompts: ["Was ziehst du im Winter an?", "Wie ist das Wetter im Herbst in Deutschland?", "Trägst du einen Regenschirm?"] },
    { title: "Im Fitnessstudio (At the Gym)", prompts: ["Bist du Mitglied in einem Fitnessstudio?", "Was machst du dort?", "Wie oft gehst du trainieren?"] },
    { title: "Deutsches Essen (German Food)", prompts: ["Welche deutschen Gerichte kennst du?", "Hast du schon Sauerkraut oder Schnitzel probiert?", "Was ist dein Lieblingsgericht aus Deutschland?"] },
    { title: "Wochenmarkt (Weekly Market)", prompts: ["Gibt es einen Markt in deiner Stadt?", "Was kaufst du dort?", "Wann ist der Markt geöffnet?"] },
    { title: "Spazierengehen (Going for a Walk)", prompts: ["Gehst du gern spazieren?", "Wo machst du Spaziergänge?", "Mit wem gehst du spazieren?"] },
    { title: "Bibliothek (Library)", prompts: ["Bist du Mitglied in einer Bibliothek?", "Was kann man in einer deutschen Bibliothek machen?", "Leihst du gern Bücher aus?"] },
    { title: "Mein Traumberuf (Dream Job)", prompts: ["Was ist dein Traumjob?", "Warum interessiert dich dieser Beruf?", "Was musst du dafür lernen?"] },
    { title: "Öffentliche Verkehrsmittel – Tickets (Transport Tickets)", prompts: ["Wie kaufst du ein Ticket in Deutschland?", "Hast du ein Monatsticket?", "Was kostet eine Fahrkarte in deiner Stadt?"] },
    { title: "Strom und Gas (Utilities)", prompts: ["Hast du schon mal einen Stromvertrag abgeschlossen?", "Was sind typische Nebenkosten?", "Wie sparst du Energie?"] },
    { title: "Meine Sprachen (My Languages)", prompts: ["Welche Sprachen sprichst du?", "Wie lernst du Deutsch?", "Welche Sprache lernst du als Nächstes?"] },
    { title: "Krankenversicherung (Health Insurance)", prompts: ["Hast du eine Krankenversicherung in Deutschland?", "Wie funktioniert das Versicherungssystem?", "Was ist der Unterschied zwischen gesetzlich und privat?"] },
    { title: "Freundschaft schließen (Making Friends)", prompts: ["Wie hast du Freunde in Deutschland gefunden?", "Was machst du, um neue Leute kennenzulernen?", "Ist es schwer, Deutsche als Freunde zu gewinnen?"] },
    { title: "Wohnungseinrichtung (Furnishing)", prompts: ["Wie hast du deine Wohnung eingerichtet?", "Was hast du bei IKEA oder ähnlichen Märkten gekauft?", "Was fehlt noch in deiner Wohnung?"] },
    { title: "Apotheke und Gesundheit (Pharmacy and Health)", prompts: ["Gehst du oft in die Apotheke?", "Was kaufst du dort?", "Brauchst du ein Rezept für Medikamente?"] },
    { title: "Jahresrückblick (Year in Review)", prompts: ["Was war dein bestes Erlebnis dieses Jahr?", "Was hast du dieses Jahr gelernt?", "Was möchtest du nächstes Jahr anders machen?"] },
    { title: "Freizeitpark und Ausflüge (Day Trips)", prompts: ["Warst du schon auf einem Ausflug in Deutschland?", "Wohin bist du gefahren?", "Was hat dir am besten gefallen?"] },
    { title: "Online-Shopping", prompts: ["Kaufst du oft online ein?", "Welche Webseiten nutzt du?", "Was hast du zuletzt online gekauft?"] },
    { title: "Mein Heimatland (My Home Country)", prompts: ["Woher kommst du?", "Was vermisst du an deiner Heimat?", "Was ist typisch für dein Land?"] },
    { title: "Kulturschock (Culture Shock)", prompts: ["Hast du einen Kulturschock erlebt?", "Was hat dich am meisten überrascht?", "Was hast du schnell gelernt?"] },
    { title: "Kochrezept (Recipe)", prompts: ["Was kochst du gern?", "Kannst du ein einfaches Rezept beschreiben?", "Welche Zutaten brauchst du?"] },
    { title: "Mein Deutschkurs (My German Course)", prompts: ["Machst du gerade einen Deutschkurs?", "Was gefällt dir am Kurs?", "Was lernst du gerade?"] },
    { title: "Versicherungen (Insurance)", prompts: ["Welche Versicherungen hast du in Deutschland?", "Was ist eine Haftpflichtversicherung?", "Warum sind Versicherungen wichtig?"] },
    { title: "Brief oder E-Mail schreiben (Writing Letters)", prompts: ["Schreibst du manchmal Briefe oder E-Mails auf Deutsch?", "Was ist der Unterschied zwischen formell und informell?", "Wie beginnst du eine formelle E-Mail?"] },
    { title: "Fußball und Sport in Deutschland (Football)", prompts: ["Ist Fußball in Deutschland beliebt?", "Schaust du Bundesliga?", "Hast du schon ein Spiel gesehen?"] },
    { title: "Geburtstag feiern in Deutschland (Birthdays)", prompts: ["Wie feiert man Geburtstag in Deutschland?", "Was schenkt man normalerweise?", "Hast du schon mal einen deutschen Geburtstag gefeiert?"] },
    { title: "Miete und Nebenkosten (Rent and Bills)", prompts: ["Wie viel Miete zahlst du?", "Was sind Nebenkosten?", "Wie bezahlst du deine Rechnungen?"] },
    { title: "Sprachprüfung (Language Exam)", prompts: ["Hast du eine Deutschprüfung gemacht?", "Wie hast du dich vorbereitet?", "Was war am schwersten in der Prüfung?"] },
    { title: "Meine Erwartungen an Deutschland (Expectations)", prompts: ["Was hast du vor der Ankunft über Deutschland gedacht?", "Was war anders als erwartet?", "Was würdest du jemandem sagen, der nach Deutschland kommt?"] },
  ],
  B1: [
    { title: "Umwelt und Klimawandel", prompts: ["Was machst du persönlich für die Umwelt?", "Wie wichtig ist Klimaschutz für dich?", "Was sollte die Regierung tun?"] },
    { title: "Soziale Medien", prompts: ["Welche sozialen Medien nutzt du?", "Was sind die Vor- und Nachteile?", "Wie viel Zeit verbringst du online?"] },
    { title: "Integration in Deutschland", prompts: ["Wie war deine Erfahrung beim Einleben?", "Was hat dir geholfen?", "Was war die größte Herausforderung?"] },
    { title: "Bildungssystem", prompts: ["Wie ist das Bildungssystem in deiner Heimat?", "Was findest du am deutschen System besonders?", "Was würdest du verbessern?"] },
    { title: "Gesundheitssystem", prompts: ["Wie funktioniert das Gesundheitssystem in Deutschland?", "Was sind deine Erfahrungen damit?", "Wie ist es in deiner Heimat?"] },
    { title: "Berufsleben und Work-Life-Balance", prompts: ["Wie ist die Work-Life-Balance in Deutschland?", "Machst du Überstunden?", "Wie trennst du Arbeit und Freizeit?"] },
    { title: "Technologie im Alltag", prompts: ["Wie nutzt du Technologie im Alltag?", "Kann man heute ohne Smartphone leben?", "Was sind die Risiken?"] },
    { title: "Ernährung und Lebensstil", prompts: ["Achtest du auf gesunde Ernährung?", "Was denkst du über vegetarische Ernährung?", "Wie hat sich dein Lebensstil in Deutschland verändert?"] },
    { title: "Reisen und interkulturelle Erfahrungen", prompts: ["Was lernst du durch Reisen?", "Welches Land möchtest du besuchen?", "Wie verändert Reisen den Blickwinkel?"] },
    { title: "Sprache und Kommunikation", prompts: ["Wie hilft Sprache bei der Integration?", "Welche Probleme hattest du mit dem Deutschen?", "Wie motivierst du dich weiter zu lernen?"] },
    { title: "Wohnungssuche in Deutschland", prompts: ["Wie hast du deine Wohnung gefunden?", "Was sind die größten Herausforderungen bei der Wohnungssuche?", "Was sollte man beim Mietvertrag beachten?"] },
    { title: "Nachhaltigkeit im Alltag (Sustainability)", prompts: ["Wie lebst du nachhaltig?", "Trennst du deinen Müll?", "Was kannst du im Alltag für die Umwelt tun?"] },
    { title: "Arbeitssuche in Deutschland (Job Search)", prompts: ["Wie hast du Arbeit in Deutschland gefunden?", "Was ist beim deutschen Lebenslauf wichtig?", "Wie war dein erstes Vorstellungsgespräch?"] },
    { title: "Wohngemeinschaft (Shared Flat)", prompts: ["Lebst du in einer WG?", "Was sind die Vor- und Nachteile einer WG?", "Wie löst man Konflikte in einer WG?"] },
    { title: "Deutsches Rechtssystem (Legal System)", prompts: ["Welche Rechte haben Migranten in Deutschland?", "Hast du schon rechtliche Unterstützung gebraucht?", "Was weißt du über das deutsche Grundgesetz?"] },
    { title: "Multikulturalismus in Deutschland", prompts: ["Wie vielfältig ist deine Stadt?", "Was lernst du von anderen Kulturen?", "Welche Herausforderungen bringt eine multikulturelle Gesellschaft?"] },
    { title: "Lokalpolitik und Gemeinde (Local Politics)", prompts: ["Interessierst du dich für lokale Politik?", "Wie kann man sich in der Gemeinde engagieren?", "Was würdest du in deiner Stadt verbessern?"] },
    { title: "Sport und Vereinskultur in Deutschland", prompts: ["Bist du in einem Sportverein?", "Welchen Stellenwert hat Sport in Deutschland?", "Welchen Sport möchtest du ausprobieren?"] },
    { title: "Kunst und Kultur in Deutschland", prompts: ["Hast du schon ein Museum oder Theater besucht?", "Welche deutschen Künstler kennst du?", "Wie wichtig ist Kultur für die Integration?"] },
    { title: "Finanzplanung im Alltag (Financial Planning)", prompts: ["Wie planst du dein monatliches Budget?", "Sparst du regelmäßig?", "Was sind typische Kosten in Deutschland?"] },
    { title: "Städtisches vs. ländliches Leben (City vs. Rural)", prompts: ["Lebst du lieber in der Stadt oder auf dem Land?", "Was sind die Unterschiede?", "Würdest du aufs Land ziehen?"] },
    { title: "Ehrenamt und Freiwilligenarbeit (Volunteering)", prompts: ["Machst du ehrenamtliche Arbeit?", "Welche Möglichkeiten gibt es in Deutschland?", "Warum ist Ehrenamt wichtig?"] },
    { title: "Digitales Leben und Datenschutz", prompts: ["Wie schützt du deine Daten online?", "Was denkst du über Social-Media-Datenschutz?", "Welche digitalen Dienste nutzt du täglich?"] },
    { title: "Freizeit und psychische Gesundheit", prompts: ["Wie erholt du dich vom Stress?", "Was tust du für dein Wohlbefinden?", "Wie wichtig ist Freizeit für die Gesundheit?"] },
    { title: "Sprache und Identität", prompts: ["Verändert sich deine Identität durch das Deutschlernen?", "Denkst du manchmal auf Deutsch?", "Welche Sprache nutzt du für Emotionen?"] },
    { title: "Deutsches Schulsystem für Kinder", prompts: ["Wie funktioniert das Schulsystem in Deutschland?", "Was sind die Unterschiede zu deinem Heimatland?", "Welche Schule ist für Kinder am besten?"] },
    { title: "Konfliktlösung im Alltag (Conflict Resolution)", prompts: ["Wie löst du Konflikte mit Nachbarn oder Kollegen?", "Wie ist die deutsche Kommunikationskultur?", "Hast du schon einen Konflikt auf Deutsch gelöst?"] },
    { title: "Pendeln und Mobilität (Commuting)", prompts: ["Wie lange pendelt du täglich?", "Nutzt du eher Auto oder ÖPNV?", "Was macht das Pendeln stressig?"] },
    { title: "Interkulturelles Verständnis (Intercultural Understanding)", prompts: ["Was hast du über die deutsche Kultur gelernt?", "Was hat dich am meisten überrascht?", "Wie kannst du andere Kulturen besser verstehen?"] },
    { title: "Diskriminierung und Rassismus (Discrimination)", prompts: ["Hast du Diskriminierung in Deutschland erlebt?", "Wie reagierst du darauf?", "Was kann die Gesellschaft dagegen tun?"] },
    { title: "Karriereplanung (Career Planning)", prompts: ["Welche Karriereziele hast du?", "Wie planst du deine berufliche Zukunft in Deutschland?", "Was sind die Herausforderungen auf dem deutschen Arbeitsmarkt?"] },
    { title: "Psychische Gesundheit (Mental Health)", prompts: ["Wie gehst du mit Stress um?", "Was tust du, wenn du dich überfordert fühlst?", "Wie wichtig ist psychische Gesundheit für dich?"] },
    { title: "Digitale Kommunikation (Digital Communication)", prompts: ["Wie kommunizierst du mit Kollegen oder Freunden digital?", "Was sind die Vor- und Nachteile von Videokonferenzen?", "Nutzt du lieber E-Mail, Chat oder Telefon?"] },
    { title: "Konsumverhalten (Consumer Behaviour)", prompts: ["Wie triffst du Kaufentscheidungen?", "Kaufst du lieber Qualität oder günstige Produkte?", "Wie hat sich dein Konsumverhalten in Deutschland verändert?"] },
    { title: "Generationsunterschiede (Generation Gap)", prompts: ["Was sind die Unterschiede zwischen älteren und jüngeren Generationen?", "Wie gehst du mit diesen Unterschieden um?", "Was können Generationen voneinander lernen?"] },
    { title: "Bürokratie in Deutschland (Bureaucracy)", prompts: ["Welche Erfahrungen hast du mit deutscher Bürokratie gemacht?", "Was findest du kompliziert?", "Wie bereitest du dich auf Behördengänge vor?"] },
    { title: "Freiwilliges soziales Jahr (Volunteering Year)", prompts: ["Kennst du das Konzept des freiwilligen sozialen Jahres?", "Würdest du gern ehrenamtlich arbeiten?", "Was kann man durch Freiwilligenarbeit lernen?"] },
    { title: "Wohneigentum vs. Miete (Buying vs. Renting)", prompts: ["Ist es in Deutschland besser zu mieten oder zu kaufen?", "Was sind die Kosten für eine eigene Wohnung?", "Möchtest du eines Tages eine eigene Wohnung kaufen?"] },
    { title: "Gastfreundschaft (Hospitality)", prompts: ["Wie ist die Gastfreundschaft in Deutschland im Vergleich zu deiner Heimat?", "Was machst du, wenn Gäste kommen?", "Was schätzt du an der deutschen Gastfreundschaft?"] },
    { title: "Sprachbarrieren (Language Barriers)", prompts: ["In welchen Situationen hast du Sprachprobleme gehabt?", "Wie hast du sie gelöst?", "Was hilft dir, Sprachbarrieren zu überwinden?"] },
    { title: "Heimweh (Homesickness)", prompts: ["Hast du Heimweh?", "Was vermisst du am meisten?", "Was hilft dir gegen Heimweh?"] },
    { title: "Soziales Netzwerk aufbauen (Building a Social Network)", prompts: ["Wie baust du soziale Kontakte in Deutschland auf?", "Welche Schwierigkeiten gibt es dabei?", "Was hilft dir, neue Freundschaften zu schließen?"] },
    { title: "Ernährungsgewohnheiten (Eating Habits)", prompts: ["Wie haben sich deine Essgewohnheiten in Deutschland verändert?", "Isst du gesünder oder ungesünder als früher?", "Was vermisst du aus der Küche deiner Heimat?"] },
    { title: "Bildung und Weiterbildung (Further Education)", prompts: ["Möchtest du in Deutschland eine Weiterbildung machen?", "Welche Möglichkeiten gibt es?", "Wie wichtig ist lebenslanges Lernen für dich?"] },
    { title: "Stereotypen (Stereotypes)", prompts: ["Welche Stereotypen über Deutsche kennst du?", "Stimmen sie?", "Welche Klischees über dein Heimatland gibt es?"] },
    { title: "Mobilität und Flexibilität (Mobility)", prompts: ["Würdest du für einen Job in eine andere Stadt ziehen?", "Wie wichtig ist Flexibilität im Beruf?", "Was würde dich zurückhalten umzuziehen?"] },
    { title: "Work-Life-Balance im Alltag", prompts: ["Wie trennst du Arbeit und Privatleben?", "Nimmst du dir genug Auszeit?", "Was machst du nach der Arbeit zur Entspannung?"] },
    { title: "Kulturelle Missverständnisse (Cultural Misunderstandings)", prompts: ["Hast du mal etwas Falsches in Deutschland getan, weil du die Kultur nicht kannt?", "Was war das Missverständnis?", "Was hast du daraus gelernt?"] },
    { title: "Klimawandel im Alltag (Climate Change in Daily Life)", prompts: ["Was merkst du vom Klimawandel in Deutschland?", "Was änderst du in deinem Alltag für die Umwelt?", "Was sollte dringend verbessert werden?"] },
    { title: "Vereine und Clubs (Clubs and Associations)", prompts: ["Bist du in einem Verein?", "Was für Vereine gibt es in Deutschland?", "Warum sind Vereine in Deutschland so wichtig?"] },
    { title: "Gesundheitsbewusstsein (Health Awareness)", prompts: ["Achtest du auf deine Gesundheit?", "Was tust du aktiv für deine Gesundheit?", "Was sollte man in Deutschland über Vorsorge wissen?"] },
    { title: "Sprache und Humor (Language and Humour)", prompts: ["Verstehst du deutschen Humor?", "Gibt es Witze, die du nicht verstehst?", "Wie unterscheidet sich der Humor in deiner Heimat?"] },
    { title: "Aufenthaltstitel und Visum (Residence Permit)", prompts: ["Welchen Aufenthaltstitel hast du?", "Wie hast du ihn bekommen?", "Was sind die Schritte zur Niederlassungserlaubnis?"] },
    { title: "Selbstständigkeit in Deutschland (Self-Employment)", prompts: ["Denkst du daran, dich selbstständig zu machen?", "Was braucht man dafür in Deutschland?", "Was sind die Risiken und Chancen?"] },
    { title: "Medien auf Deutsch (German Media)", prompts: ["Schaust du deutsches Fernsehen oder hörst du Radio?", "Welche Sendungen oder Podcasts magst du?", "Hilft dir deutschen Medien beim Sprachlernen?"] },
    { title: "Naturkatastrophen und Sicherheit (Natural Disasters)", prompts: ["Welche Naturkatastrophen gibt es in Deutschland?", "Wie bereitet man sich darauf vor?", "Hast du schon mal extreme Wetterbedingungen erlebt?"] },
    { title: "Persönliche Entwicklung (Personal Growth)", prompts: ["Was hat dich in Deutschland am meisten verändert?", "Was hast du über dich selbst gelernt?", "Wie bist du als Person gewachsen?"] },
    { title: "Digitale Behördengänge (Online Government Services)", prompts: ["Welche Behördendienste kann man online nutzen?", "Hast du schon mal etwas online beantragt?", "Was fehlt noch bei der Digitalisierung von Behörden?"] },
    { title: "Fremdsprachenkenntnisse im Beruf (Languages at Work)", prompts: ["Brauchst du Deutsch oder andere Sprachen bei der Arbeit?", "Wie hilfreich sind mehrere Sprachen im Beruf?", "Was machst du, wenn du ein Wort nicht weißt?"] },
    { title: "Mein Fortschritt auf Deutsch (My German Progress)", prompts: ["Wie hat sich dein Deutsch verbessert?", "Was kannst du jetzt, was du früher nicht konntest?", "Worauf bist du am meisten stolz?"] },
  ],
  B2: [
    { title: "Politik und Gesellschaft", prompts: ["Wie funktioniert die deutsche Demokratie?", "Was hältst du von der aktuellen Politik?", "Wie engagierst du dich gesellschaftlich?"] },
    { title: "Globalisierung und ihre Folgen", prompts: ["Was sind die Chancen der Globalisierung?", "Welche Risiken siehst du?", "Wie beeinflusst sie deinen Alltag?"] },
    { title: "Digitalisierung und Arbeitswelt", prompts: ["Wie verändert KI die Arbeitswelt?", "Welche Berufe sind gefährdet?", "Wie bereitet man sich vor?"] },
    { title: "Migration und Integration", prompts: ["Was sind die Herausforderungen für Migranten?", "Welche Verantwortung hat die Gesellschaft?", "Was kann jeder Einzelne tun?"] },
    { title: "Kunst, Kultur und Identität", prompts: ["Welche Rolle spielt Kultur bei der Identität?", "Wie hat Deutschland deine Sichtweise geprägt?", "Welche Kunstform schätzt du?"] },
    { title: "Wirtschaft und soziale Gerechtigkeit", prompts: ["Wie fair ist das Wirtschaftssystem?", "Was denkst du über den Mindestlohn?", "Wie kann Chancengleichheit erreicht werden?"] },
    { title: "Wissenschaft und ethische Fragen", prompts: ["Wo liegen die Grenzen der Wissenschaft?", "Was hältst du von gentechnischen Eingriffen?", "Welche Forschung ist am wichtigsten?"] },
    { title: "Medien und Meinungsbildung", prompts: ["Wie bildest du dir eine Meinung?", "Welchen Medien vertraust du?", "Was ist Fake News und wie erkennt man sie?"] },
    { title: "Chancen und Herausforderungen als Immigrant", prompts: ["Was hast du durch die Migration gewonnen?", "Was war der schwerste Moment?", "Was gibst du an zukünftige Migranten weiter?"] },
    { title: "Zukunft und persönliche Ziele", prompts: ["Wo siehst du dich in 5 Jahren?", "Was möchtest du in Deutschland erreichen?", "Was motiviert dich jeden Tag?"] },
    { title: "Soziale Ungleichheit (Social Inequality)", prompts: ["Wie zeigt sich soziale Ungleichheit in Deutschland?", "Was sind die Ursachen von Armut?", "Welche Maßnahmen helfen wirklich?"] },
    { title: "Sprachpolitik und Mehrsprachigkeit", prompts: ["Welche Rolle spielt Englisch als Weltsprache?", "Sollte Mehrsprachigkeit stärker gefördert werden?", "Wie verändert Sprache das Denken?"] },
    { title: "Künstliche Intelligenz und Gesellschaft", prompts: ["Welche ethischen Fragen stellt KI?", "Wie verändert KI den Arbeitsmarkt?", "Sollte KI reguliert werden?"] },
    { title: "Demokratie und Populismus", prompts: ["Was gefährdet die Demokratie?", "Wie erklärt du den Aufstieg populistischer Parteien?", "Was können Bürger tun?"] },
    { title: "Nachhaltigkeit und Wirtschaft (Green Economy)", prompts: ["Wie vereint man Wirtschaftswachstum und Umweltschutz?", "Was ist die Rolle der Unternehmen?", "Konsumierst du bewusst?"] },
    { title: "Bildungsgerechtigkeit (Educational Equality)", prompts: ["Hat jedes Kind in Deutschland gleiche Chancen?", "Welche Rolle spielt der soziale Hintergrund?", "Was muss das System ändern?"] },
    { title: "Pressefreiheit und Medienlandschaft", prompts: ["Wie wichtig ist eine freie Presse?", "Welchen Medien vertraust du und warum?", "Wie erkennst du Desinformation?"] },
    { title: "Digitale Transformation der Arbeit", prompts: ["Wie verändert Homeoffice die Arbeitskultur?", "Was sind die Vor- und Nachteile des digitalen Arbeitens?", "Brauchen wir neue Arbeitsgesetze?"] },
    { title: "Europäische Union und Identität", prompts: ["Was bedeutet es, Europäer zu sein?", "Welche Vorteile hat die EU?", "Wo siehst du Reformbedarf in der EU?"] },
    { title: "Philosophie des Glücks (Philosophy of Happiness)", prompts: ["Was bedeutet Glück für dich?", "Kann man Glück lernen?", "Was sagt die Philosophie über ein gutes Leben?"] },
    { title: "Urbanisierung und Stadtplanung", prompts: ["Wie verändert Urbanisierung das Leben?", "Was sind die Probleme wachsender Städte?", "Wie sollte eine ideale Stadt aussehen?"] },
    { title: "Trauma und Resilienz bei Migration", prompts: ["Welche emotionalen Herausforderungen bringt Migration?", "Was stärkt die psychische Widerstandskraft?", "Wie kann die Gesellschaft unterstützen?"] },
    { title: "Religionsfreiheit und Säkularismus", prompts: ["Welche Rolle spielt Religion in Deutschland?", "Wie verträgt sich Religion mit dem säkularen Staat?", "Was denkst du über Religionsfreiheit?"] },
    { title: "Klimapolitik und internationale Verantwortung", prompts: ["Wer trägt die größte Verantwortung für den Klimawandel?", "Reichen die aktuellen Maßnahmen aus?", "Was kannst du persönlich beitragen?"] },
    { title: "Identität zwischen zwei Kulturen", prompts: ["Fühlst du dich mehr deutsch oder aus deiner Heimatkultur?", "Wie verhandelt du Konflikte zwischen zwei Kulturen?", "Was nimmst du aus beiden Kulturen mit?"] },
    { title: "Armut und soziale Sicherheit in Deutschland", prompts: ["Wie funktioniert das Sozialsystem in Deutschland?", "Wer fällt durch das Raster?", "Was sind die Grenzen des Wohlfahrtsstaats?"] },
    { title: "Globale Gesundheit und Pandemien", prompts: ["Was hat die Corona-Pandemie gezeigt?", "Wie sollte die Welt auf künftige Pandemien reagieren?", "Welche Rolle spielen internationale Organisationen?"] },
    { title: "Literatur und gesellschaftlicher Wandel", prompts: ["Kann Literatur die Gesellschaft verändern?", "Welches Buch hat dich am meisten beeinflusst?", "Was liest du auf Deutsch?"] },
    { title: "Ethik der Technologie (Tech Ethics)", prompts: ["Wie weit darf Überwachungstechnologie gehen?", "Wer kontrolliert die großen Technologiekonzerne?", "Was sind deine digitalen Grenzen?"] },
    { title: "Persönliche Werte und moralische Entscheidungen", prompts: ["Welche Werte sind dir am wichtigsten?", "Wie triffst du schwierige moralische Entscheidungen?", "Hat Deutschland deine Werte verändert?"] },
    { title: "Kritisches Denken (Critical Thinking)", prompts: ["Wie überprüfst du Informationen, bevor du ihnen glaubst?", "Was bedeutet kritisches Denken für dich?", "Wie wichtig ist es, verschiedene Perspektiven zu kennen?"] },
    { title: "Globale Verantwortung (Global Responsibility)", prompts: ["Welche Verantwortung haben reiche Länder gegenüber ärmeren?", "Wie hängen globale Probleme zusammen?", "Was kannst du als Einzelperson tun?"] },
    { title: "Sprachliche Macht (Power of Language)", prompts: ["Wie beeinflusst Sprache unser Denken?", "Welche Wörter sollte man vermeiden?", "Wie verändert sich Sprache durch gesellschaftlichen Wandel?"] },
    { title: "Zukunft der Arbeit (Future of Work)", prompts: ["Wie wird die Arbeitswelt in 20 Jahren aussehen?", "Welche Fähigkeiten werden wichtiger?", "Bereitet das Bildungssystem gut auf die Zukunft vor?"] },
    { title: "Nationale Identität (National Identity)", prompts: ["Was bedeutet nationale Identität für dich?", "Wie hat Migration deine Identität verändert?", "Kann man zwei Kulturen gleichzeitig angehören?"] },
    { title: "Philosophie und Alltag (Philosophy in Daily Life)", prompts: ["Welche philosophischen Fragen beschäftigen dich?", "Was ist der Sinn des Lebens für dich persönlich?", "Wie beeinflusst Philosophie deine Entscheidungen?"] },
    { title: "Wirtschaftssystem im Vergleich (Economic Systems)", prompts: ["Was sind die Unterschiede zwischen Kapitalismus und Sozialismus?", "Welches System findest du gerechter?", "Wie beeinflusst das Wirtschaftssystem deinen Alltag?"] },
    { title: "Zivilcourage (Civil Courage)", prompts: ["Was verstehst du unter Zivilcourage?", "Hast du schon mal jemandem geholfen, obwohl es schwierig war?", "Wie fördert man Zivilcourage in der Gesellschaft?"] },
    { title: "Sprache und Macht (Language and Power)", prompts: ["Wer bestimmt, was als korrektes Deutsch gilt?", "Wie verändert politische Korrektheit die Sprache?", "Welche Rolle spielen Dialekte in der Gesellschaft?"] },
    { title: "Digitale Ethik (Digital Ethics)", prompts: ["Wem gehören unsere Daten im Internet?", "Wie sollte künstliche Intelligenz reguliert werden?", "Was sind die Grenzen von Automatisierung?"] },
    { title: "Friedenssicherung (Peacebuilding)", prompts: ["Was sind die Ursachen von Konflikten?", "Welche Rolle spielt Deutschland in der internationalen Diplomatie?", "Wie kann man zum Frieden beitragen?"] },
    { title: "Urbanisierung und Gentrifizierung", prompts: ["Was ist Gentrifizierung und welche Folgen hat sie?", "Wie verändert sich deine Stadt durch Zuzug?", "Wer profitiert und wer leidet darunter?"] },
    { title: "Postkolonialismus (Postcolonialism)", prompts: ["Welche Auswirkungen hat der Kolonialismus heute noch?", "Wie geht Deutschland mit seiner Kolonialgeschichte um?", "Was bedeutet Dekolonisierung?"] },
    { title: "Feminismus und Gleichstellung (Feminism and Equality)", prompts: ["Was bedeutet Gleichstellung für dich?", "Wie ist die Situation von Frauen auf dem Arbeitsmarkt?", "Was muss sich noch ändern?"] },
    { title: "Menschenrechte (Human Rights)", prompts: ["Welche Menschenrechte sind dir besonders wichtig?", "Wo werden Menschenrechte verletzt?", "Was kann Deutschland tun, um Menschenrechte zu schützen?"] },
    { title: "Soziale Medien und Demokratie", prompts: ["Wie beeinflussen soziale Medien politische Meinungen?", "Können Algorithmen die Demokratie gefährden?", "Was sind die Verantwortlichkeiten der Plattformen?"] },
    { title: "Intergenerationelle Gerechtigkeit (Intergenerational Justice)", prompts: ["Welche Verantwortung haben wir gegenüber zukünftigen Generationen?", "Ist die Rentenpolitik in Deutschland gerecht?", "Wie denken Junge und Alte darüber?"] },
    { title: "Deutsch als Fremdsprache lernen (Learning German)", prompts: ["Was sind die größten Herausforderungen beim Deutschlernen?", "Wie unterscheidet sich Deutsch von anderen Sprachen?", "Was sind deine effektivsten Lernmethoden?"] },
    { title: "Kulturelles Erbe (Cultural Heritage)", prompts: ["Was versteht man unter kulturellem Erbe?", "Wie schützt Deutschland sein Kulturerbe?", "Was ist das wichtigste Kulturerbe deines Heimatlandes?"] },
    { title: "Gesellschaftlicher Zusammenhalt (Social Cohesion)", prompts: ["Was hält eine Gesellschaft zusammen?", "Nimmt der Zusammenhalt in Deutschland zu oder ab?", "Was kannst du tun, um den Zusammenhalt zu stärken?"] },
    { title: "Konsum und Nachhaltigkeit (Consumption and Sustainability)", prompts: ["Wie beeinflusst dein Konsumverhalten die Umwelt?", "Was bedeutet Fast Fashion und was sind die Folgen?", "Wie verändert sich dein Kaufverhalten?"] },
    { title: "Rechtspopulismus (Right-wing Populism)", prompts: ["Was sind die Ursachen des Rechtspopulismus?", "Wie reagiert man auf populistische Argumente?", "Was kann die Gesellschaft tun, um Extremismus zu verhindern?"] },
    { title: "Akademische Sprache (Academic Language)", prompts: ["Wie unterscheidet sich akademisches Deutsch vom Alltagsdeutsch?", "Welche Fachbegriffe kennst du?", "Wie liest du wissenschaftliche Texte auf Deutsch?"] },
    { title: "Biografie und Lebensweg (Biography and Life Path)", prompts: ["Wie hat dein Leben dich geformt?", "Welche Entscheidung hat deinen Lebensweg am meisten beeinflusst?", "Was würdest du rückblickend anders machen?"] },
    { title: "Toleranz und Respekt (Tolerance and Respect)", prompts: ["Was bedeutet Toleranz für dich?", "Wo sind die Grenzen von Toleranz?", "Wie lebst du Toleranz im Alltag?"] },
    { title: "Kulturelle Anpassung vs. Bewahrung (Assimilation vs. Preservation)", prompts: ["Soll man sich vollständig anpassen oder seine Kultur bewahren?", "Wo ist die Balance?", "Was gibst du auf und was behältst du?"] },
    { title: "Sprache und Gefühle (Language and Emotions)", prompts: ["In welcher Sprache träumst du?", "Welche Sprache nutzt du für Emotionen?", "Verändert Deutsch deine Persönlichkeit?"] },
    { title: "Globale Ungleichheit (Global Inequality)", prompts: ["Warum sind manche Länder reicher als andere?", "Was sind die Folgen der globalen Ungleichheit?", "Welche Lösungsansätze gibt es?"] },
    { title: "Lebensqualität (Quality of Life)", prompts: ["Was macht ein gutes Leben für dich aus?", "Wie misst man Lebensqualität?", "Ist Deutschland ein gutes Land zum Leben – und warum?"] },
    { title: "Zukunft Europas (Future of Europe)", prompts: ["Wohin entwickelt sich Europa politisch?", "Welche Herausforderungen hat die EU?", "Wie siehst du deine Zukunft in Europa?"] },
  ],
};

const getLevel = (batchName: string | null): "A1" | "A2" | "B1" | "B2" => {
  if (!batchName) return "A1";
  const u = batchName.toUpperCase();
  if (u.includes("B2")) return "B2";
  if (u.includes("B1")) return "B1";
  if (u.includes("A2")) return "A2";
  return "A1";
};

const getTodaysTopic = (batchName: string | null, submissionCount: number): Topic => {
  const level = getLevel(batchName);
  const list = TOPICS[level];
  return list[submissionCount % list.length];
};

const getMinSeconds = (batchName: string | null): number => {
  if (!batchName) return 60;
  const u = batchName.toUpperCase();
  if (u.includes("B2") || u.includes("B1")) return 120;
  if (u.includes("A2")) return 90;
  return 60;
};

type Status = "DRAFT" | "SUBMITTED" | "REVIEWED";

interface Submission {
  id: number;
  sequenceNumber: number;
  topic: string | null;
  audioUrl: string | null;
  originalFileName: string | null;
  status: Status;
  uploadedAt: string | null;
  submittedAt: string | null;
  feedback: string | null;
  feedbackBy: string | null;
  feedbackAt: string | null;
  createdAt: string;
}

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "—";


const fmtTime = (s: number) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const checkAudioDuration = (blob: Blob): Promise<number> =>
  new Promise((resolve) => {
    const audio = new Audio();
    audio.src = URL.createObjectURL(blob);
    audio.onloadedmetadata = () => resolve(audio.duration);
    audio.onerror = () => resolve(0);
  });

const STATUS_CONFIG: Record<Status, { label: string; cls: string }> = {
  DRAFT:     { label: "Draft",     cls: "bg-gray-100 text-gray-500" },
  SUBMITTED: { label: "Submitted", cls: "bg-blue-100 text-blue-700" },
  REVIEWED:  { label: "Reviewed",  cls: "bg-green-100 text-green-700" },
};

// ── How It Works ─────────────────────────────────────────────────────────────
const HowItWorks: React.FC<{ minSeconds: number }> = ({ minSeconds }) => {
  const steps = [
    {
      icon: FaMicrophone,
      color: "bg-red-100 text-red-600",
      title: "Record or Upload",
      desc: "Tap Start Recording to record directly in your browser, or upload an existing audio file.",
    },
    {
      icon: FaClock,
      color: "bg-amber-100 text-amber-600",
      title: `Meet the Minimum (${minSeconds}s)`,
      desc: `Your audio must be at least ${minSeconds} seconds. The stop button unlocks once you reach that.`,
    },
    {
      icon: FaUpload,
      color: "bg-blue-100 text-blue-600",
      title: "Preview & Save",
      desc: "Listen to your recording before saving. You can re-record or re-upload as many times as you like.",
    },
    {
      icon: FaCheckCircle,
      color: "bg-green-100 text-green-600",
      title: "Submit Once Per Day",
      desc: "When you're happy with the recording, submit it. You can submit one audio per day.",
    },
    {
      icon: FaPlay,
      color: "bg-purple-100 text-purple-600",
      title: "Receive Feedback",
      desc: "Your mentor listens to the recording and leaves written feedback. After review you can submit a new one.",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <FaInfoCircle className="text-[#009fe3]" size={16} />
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">How it works</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.color}`}>
                <Icon size={13} />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800 mb-0.5">
                  <span className="text-gray-400 mr-1">{i + 1}.</span>{s.title}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Confirm Modal ─────────────────────────────────────────────────────────────
const ConfirmModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}> = ({ onConfirm, onCancel, loading, error }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <FaExclamationTriangle className="text-amber-500" size={20} />
        </div>
        <h3 className="text-base font-bold text-gray-800">Submit Recording?</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Once submitted, you can no longer replace this audio. Your mentor will review it.
        </p>
      </div>
      {error && (
        <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-700">
          <FaExclamationTriangle size={13} className="shrink-0" />
          {error}
        </div>
      )}
      <div className="flex gap-3 mt-4">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading || !!error}
          className="flex-1 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <FaSpinner className="animate-spin" size={13} /> : <FaCheckCircle size={13} />}
          Yes, Submit
        </button>
      </div>
    </div>
  </div>
);

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
const DeleteConfirmModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <FaTrash className="text-red-500" size={18} />
        </div>
        <h3 className="text-base font-bold text-gray-800">Delete Draft?</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          This empty draft will be permanently removed. This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <FaSpinner className="animate-spin" size={13} /> : <FaTrash size={13} />}
          Yes, Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const StudentAudioPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [batchName, setBatchName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [showHints, setShowHints] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  useEffect(() => {
    Promise.all([
      fetch(API, { credentials: "include" }).then((r) => r.json()),
      fetch(BATCH_API, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
    ])
      .then(([subs, batch]) => {
        setSubmissions(subs);
        setBatchName(batch?.batchName ?? null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Derived state
  const minSeconds = getMinSeconds(batchName);
  const hasDraft = submissions.some((s) => s.status === "DRAFT");
  const submittedCount = submissions.filter((s) => s.status !== "DRAFT").length;
  const todaysTopic = getTodaysTopic(batchName, submittedCount);
  const today = new Date().toDateString();
  const submittedToday = submissions.some(
    (s) => s.submittedAt && new Date(s.submittedAt).toDateString() === today
  );
  const canCreateNew = !hasDraft && !submittedToday;

  const createDraft = async () => {
    if (hasDraft) {
      const draft = submissions.find((s) => s.status === "DRAFT")!;
      setActiveId(draft.id);
      setRecordedBlob(null);
      setPreviewUrl(null);
      setDurationError(null);
      return;
    }
    if (!canCreateNew) return;
    setCreating(true);
    try {
      const r = await fetch(`${API}?topic=${encodeURIComponent(todaysTopic.title)}`, { method: "POST", credentials: "include" });
      const sub: Submission = await r.json();
      setSubmissions((prev) => [...prev, sub]);
      setActiveId(sub.id);
      setRecordedBlob(null);
      setPreviewUrl(null);
      setDurationError(null);
    } finally {
      setCreating(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordingSeconds(0);
      setDurationError(null);
      timerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } catch {
      alert("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (recordingSeconds < minSeconds) {
      setDurationError(`Recording must be at least ${minSeconds} seconds. Current: ${recordingSeconds}s.`);
      return;
    }
    mediaRecorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const uploadFile = async (id: number, file: File | Blob, name?: string) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, name ?? "recording.webm");
      const r = await fetch(`${API}/${id}/audio`, { method: "POST", credentials: "include", body: fd });
      const updated: Submission = await r.json();
      setSubmissions((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = async (_id: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDurationError(null);
    const duration = await checkAudioDuration(file);
    if (duration < minSeconds) {
      setDurationError(`Audio must be at least ${minSeconds} seconds. This file is ${Math.round(duration)}s.`);
      e.target.value = "";
      return;
    }
    setPreviewUrl(URL.createObjectURL(file));
    setRecordedBlob(file);
  };

  const handleUpload = async (id: number) => {
    if (!recordedBlob) return;
    const name = recordedBlob instanceof File ? recordedBlob.name : "recording.webm";
    await uploadFile(id, recordedBlob, name);
    setRecordedBlob(null);
    setPreviewUrl(null);
  };

  const confirmSubmit = async () => {
    if (!confirmId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch(`${API}/${confirmId}/submit`, { method: "POST", credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        const msg = body?.message ?? body?.error ?? "Submission failed. Please try again.";
        setSubmitError(msg);
        return;
      }
      const updated: Submission = await r.json();
      setSubmissions((prev) => prev.map((s) => (s.id === confirmId ? updated : s)));
      setActiveId(null);
      setConfirmId(null);
    } finally {
      setSubmitting(false);
    }
  };

  const togglePlay = (id: number, url: string) => {
    if (playingId === id) {
      audioRefs.current.get(id)?.pause();
      setPlayingId(null);
    } else {
      if (playingId !== null) audioRefs.current.get(playingId)?.pause();
      let audio = audioRefs.current.get(id);
      if (!audio) {
        audio = new Audio(url);
        audio.onended = () => setPlayingId(null);
        audioRefs.current.set(id, audio);
      }
      audio.play();
      setPlayingId(id);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await fetch(`${API}/${deleteConfirmId}`, { method: "DELETE", credentials: "include" });
      setSubmissions((prev) => prev.filter((s) => s.id !== deleteConfirmId));
      if (activeId === deleteConfirmId) {
        setActiveId(null);
        setRecordedBlob(null);
        setPreviewUrl(null);
        setDurationError(null);
      }
      setDeleteConfirmId(null);
    } finally {
      setDeleting(false);
    }
  };

  const closePanel = () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      setRecording(false);
      setRecordingSeconds(0);
    }
    setActiveId(null);
    setRecordedBlob(null);
    setPreviewUrl(null);
    setDurationError(null);
  };

  // Header button
  const headerBtn = hasDraft
    ? { label: "Continue Draft", action: createDraft, disabled: false }
    : submittedToday
    ? { label: "Submitted Today", action: () => {}, disabled: true }
    : { label: "New Recording", action: createDraft, disabled: creating };

  return (
    <div className="min-h-screen bg-gray-100">
      {confirmId && <ConfirmModal onConfirm={confirmSubmit} onCancel={() => { setConfirmId(null); setSubmitError(null); }} loading={submitting} error={submitError} />}
      {deleteConfirmId && <DeleteConfirmModal onConfirm={confirmDelete} onCancel={() => setDeleteConfirmId(null)} loading={deleting} />}

      <PageHeader
        icon={<FaMicrophone />}
        title="Speaking Practice"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Speaking Practice" },
        ]}
      />

      {/* Action toolbar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 flex justify-end">
          <button
            onClick={headerBtn.action}
            disabled={headerBtn.disabled}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold shadow transition-colors shrink-0 disabled:opacity-60 ${
              submittedToday
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#009fe3] text-white hover:bg-[#0088cc]"
            }`}
          >
            {creating ? <FaSpinner className="animate-spin" size={12} /> : <FaMicrophone size={12} />}
            <span className="hidden sm:inline">{headerBtn.label}</span>
            <span className="sm:hidden">{hasDraft ? "Draft" : submittedToday ? "Done" : "New"}</span>
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {loading ? (
          <div className="flex justify-center py-24">
            <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="space-y-4">
            {/* Motivational banner */}
            <div className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] rounded-2xl p-5 text-white">
              <p className="text-lg font-bold mb-1">Sprechen macht den Meister! 🎙️</p>
              <p className="text-sm text-white/90 leading-relaxed">
                Just one minute of speaking every day builds real confidence. Record, listen, improve — your mentor is here to guide you every step of the way.
              </p>
            </div>
            {/* Today's topic */}
            <div className="bg-gradient-to-br from-[#009fe3]/10 to-blue-50 border border-[#009fe3]/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#009fe3]">Today's Topic</span>
              </div>
              <p className="text-base font-bold text-gray-800 mb-3">{todaysTopic.title}</p>
              <ul className="space-y-1.5">
                {todaysTopic.prompts.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="mt-0.5 w-4 h-4 rounded-full bg-[#009fe3]/20 text-[#009fe3] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <HowItWorks minSeconds={minSeconds} />
            <div className="bg-white rounded-2xl border border-gray-200 py-10 text-center">
              <FaMicrophone className="mx-auto text-gray-300 mb-3" size={36} />
              <p className="text-gray-500 font-medium">No recordings yet</p>
              <p className="text-gray-400 text-sm mt-1 mb-5">Tap "New Recording" above to get started.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Motivational banner */}
            <div className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] rounded-2xl px-5 py-3.5 text-white flex items-center gap-3">
              <FaMicrophone size={18} className="shrink-0 text-white/80" />
              <p className="text-sm font-medium text-white/95 leading-snug">
                <span className="font-bold text-white">Sprechen macht den Meister!</span> Keep recording daily — every session makes you more confident.
              </p>
            </div>
            {/* Today's / Tomorrow's topic */}
            {submittedToday ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-green-600">Great job! Come back tomorrow</span>
                </div>
                <p className="text-xs text-green-700 mb-3">You've already submitted today's recording. Tomorrow's topic will be:</p>
                <p className="text-sm font-bold text-gray-800 mb-2">{todaysTopic.title}</p>
                <ul className="space-y-1">
                  {todaysTopic.prompts.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                      <span className="mt-0.5 w-3.5 h-3.5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#009fe3]/10 to-blue-50 border border-[#009fe3]/30 rounded-2xl p-4">
                <span className="text-xs font-bold uppercase tracking-widest text-[#009fe3]">Today's Topic</span>
                <p className="text-sm font-bold text-gray-800 mt-1 mb-2">{todaysTopic.title}</p>
                <ul className="space-y-1">
                  {todaysTopic.prompts.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="mt-0.5 w-3.5 h-3.5 rounded-full bg-[#009fe3]/20 text-[#009fe3] flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Collapsible hints */}
            <button
              onClick={() => setShowHints((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <span className="flex items-center gap-2 font-medium">
                <FaInfoCircle className="text-[#009fe3]" size={13} /> How it works
              </span>
              {showHints ? <FaChevronUp size={11} className="text-gray-400" /> : <FaChevronDown size={11} className="text-gray-400" />}
            </button>
            {showHints && <HowItWorks minSeconds={minSeconds} />}
            {[...submissions].reverse().map((sub) => {
              const { label, cls } = STATUS_CONFIG[sub.status];
              const isActive = activeId === sub.id;
              return (
                <div
                  key={sub.id}
                  className={`bg-white rounded-2xl border shadow-sm transition-all ${
                    isActive ? "border-[#009fe3]/50 shadow-md" : "border-gray-200"
                  }`}
                >
                  {/* Card header */}
                  <div className="flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4">
                    <div className="w-9 h-9 rounded-full bg-[#009fe3]/10 text-[#009fe3] flex items-center justify-center text-sm font-bold shrink-0">
                      {sub.sequenceNumber}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-800">Recording #{sub.sequenceNumber}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
                      </div>
                      {sub.topic && (
                        <div className="text-xs font-medium text-[#009fe3] mt-0.5 truncate">{sub.topic}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-0.5 truncate">
                        {fmtDate(sub.uploadedAt ?? sub.createdAt)}
                        {sub.originalFileName && <span className="ml-1.5 text-gray-300">· {sub.originalFileName}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {sub.audioUrl && (
                        <button
                          onClick={() => togglePlay(sub.id, sub.audioUrl!)}
                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                            playingId === sub.id ? "bg-[#009fe3] text-white" : "bg-blue-50 hover:bg-blue-100 text-[#009fe3]"
                          }`}
                        >
                          {playingId === sub.id ? <FaPause size={12} /> : <FaPlay size={12} />}
                        </button>
                      )}
                      {sub.status === "DRAFT" && !isActive && (
                        <>
                          <button
                            onClick={() => { setActiveId(sub.id); setDurationError(null); }}
                            className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
                          >
                            <FaUpload size={10} />
                            <span className="hidden sm:inline">{sub.audioUrl ? "Edit / Submit" : "Upload / Record"}</span>
                            <span className="sm:hidden">{sub.audioUrl ? "Edit" : "Upload"}</span>
                          </button>
                          {!sub.audioUrl && (
                            <button
                              onClick={() => setDeleteConfirmId(sub.id)}
                              title="Delete this empty draft"
                              className="w-8 h-8 rounded-full flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
                            >
                              <FaTrash size={11} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Feedback */}
                  {sub.feedback && (
                    <div className="px-4 sm:px-5 pb-4">
                      <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 text-sm text-gray-700">
                        <span className="font-semibold text-green-700">Feedback: </span>
                        {sub.feedback}
                      </div>
                    </div>
                  )}

                  {/* Upload / Record panel */}
                  {isActive && (
                    <div className="border-t border-gray-100 px-4 sm:px-5 py-4 bg-gray-50 rounded-b-2xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                          <FaClock size={11} className="shrink-0" />
                          Minimum {minSeconds} seconds required
                        </div>
                        <button
                          onClick={closePanel}
                          className="px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors"
                        >
                          Close
                        </button>
                      </div>

                      {/* Duration error */}
                      {durationError && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-700">
                          <FaExclamationTriangle size={13} className="shrink-0" />
                          {durationError}
                        </div>
                      )}

                      {/* Record / Upload buttons */}
                      <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                          {!recording ? (
                            <button
                              onClick={startRecording}
                              className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm flex-1"
                            >
                              <FaMicrophone size={14} /> Start Recording
                            </button>
                          ) : (
                            <div className="flex flex-col gap-2 flex-1">
                              <div className="flex items-center justify-between px-1">
                                <span className={`text-sm font-mono font-bold ${recordingSeconds >= minSeconds ? "text-green-600" : "text-red-500"}`}>
                                  {fmtTime(recordingSeconds)}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {recordingSeconds < minSeconds
                                    ? `${minSeconds - recordingSeconds}s until ready to stop`
                                    : "Ready to stop"}
                                </span>
                              </div>
                              <button
                                onClick={stopRecording}
                                disabled={recordingSeconds < minSeconds}
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <FaStop size={14} />
                                {recordingSeconds < minSeconds ? `Stop (${minSeconds - recordingSeconds}s left)` : `Stop Recording (${minSeconds}s reached)`}
                              </button>
                            </div>
                          )}
                          {!recording && (
                            <label className="flex items-center justify-center gap-2 px-4 py-3 bg-[#009fe3] hover:bg-[#0088cc] text-white rounded-xl text-sm font-semibold cursor-pointer transition-colors shadow-sm flex-1">
                              <FaUpload size={14} /> Upload File
                              <input type="file" accept="audio/*" className="hidden" onChange={(e) => handleFileInput(sub.id, e)} />
                            </label>
                          )}
                        </div>
                      </div>

                      {/* Submit — shown when audio saved and no new recording pending */}
                      {sub.audioUrl && !previewUrl && !recording && (
                        <button
                          onClick={() => setConfirmId(sub.id)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm"
                        >
                          <FaCheckCircle size={13} /> Submit Recording
                        </button>
                      )}

                      {/* Preview */}
                      {previewUrl && (
                        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4">
                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Preview</p>
                          <audio controls src={previewUrl} className="w-full h-10" />
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => handleUpload(sub.id)}
                              disabled={uploading}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#009fe3] hover:bg-[#0088cc] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                            >
                              {uploading ? <FaSpinner className="animate-spin" size={13} /> : <FaUpload size={13} />}
                              {sub.audioUrl ? "Replace Audio" : "Save Audio"}
                            </button>
                            <button
                              onClick={() => { setRecordedBlob(null); setPreviewUrl(null); setDurationError(null); }}
                              className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                              title="Discard"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentAudioPage;
